<template>
  <span class="navbar-item has-dropdown">
    <span class="goto-input">
      <input
        class="input"
        ref="inputElement"
        placeholder="Go to…"
        v-model.trim="input"
        v-on:keyup="navigate"
      />
      <kbd>g</kbd>
    </span>

    <div class="navbar-dropdown">
      <component
        v-for="(shortcut, i) in matchedShortcuts"
        :key="shortcut.keyword"
        :is="shortcut.route ? 'router-link' : 'a'"
        v-bind="shortcutProps(shortcut)"
        class="navbar-item"
        :class="{ 'is-active': selectedShortcut === i }"
      >
        {{ shortcut.prefix }}<strong>{{ shortcut.keyword }}</strong
        >{{ shortcut.suffix }}
      </component>
    </div>
  </span>
</template>

<script setup lang="ts">
import Mousetrap from 'mousetrap'
import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue'
import { type RouteLocationRaw, useRouter } from 'vue-router'

import { UUID_REGEX } from '@/config'
import { useAuthStore } from '@/stores'

interface Shortcut {
  /**
   * Optional text displayed before the keyword. There are no spaces separating it from the keyword.
   */
  prefix?: string

  /**
   * Keyword displayed to the user.
   *
   * When it is set by `availableShortcuts`, it will also be used by `matchedShortcuts` to match it against the user's input.
   */
  keyword?: string

  /**
   * Optional text displayed after the keyword. There are no spaces separating it from the keyword.
   */
  suffix?: string

  /**
   * Have this item link to a route from Vue Router.
   */
  route?: RouteLocationRaw

  /**
   * Have this item link to a URL outside of the frontend.
   */
  externalLink?: string
}

const PARTIAL_UUID_REGEX =
  /^[0-9a-f]{1,8}(?:-[0-9a-f]{0,4}(?:-[0-9a-f]{0,4}(?:-[0-9a-f]{0,4}(?:-[0-9a-f]{0,12})?)?)?)?$/i
const PLACEHOLDER_UUID = 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'

const authStore = useAuthStore()
const router = useRouter()

const input = ref<string>('')
const inputElement = useTemplateRef('inputElement')

const availableShortcuts = computed<Shortcut[]>(() => {
  const shortcuts: Shortcut[] = [
    {
      prefix: 'Paste an element ID to view it',
    },
    {
      prefix: 'Go to ',
      keyword: 'projects',
      route: { name: 'corpus-list' },
    },
  ]
  if (authStore.isVerified) {
    shortcuts.push({
      prefix: 'Go to ',
      keyword: 'processes',
      route: { name: 'processes-list' },
    })
  }
  shortcuts.push({
    prefix: 'Browse ',
    keyword: 'documentation',
    externalLink: 'https://doc.teklia.com',
  })
  return shortcuts
})

const matchedShortcuts = computed<Shortcut[]>(() => {
  // Default to displaying everything
  if (!input.value) return availableShortcuts.value

  const shortcuts = []

  if (UUID_REGEX.test(input.value)) {
    shortcuts.push({
      prefix: 'View element ',
      keyword: input.value,
      route: {
        name: 'element-details',
        params: { id: input.value },
      },
    })
  } else if (PARTIAL_UUID_REGEX.test(input.value)) {
    shortcuts.push({
      prefix: 'View element ',
      keyword: input.value,
      suffix: PLACEHOLDER_UUID.slice(input.value.length),
    })
  }

  for (const shortcut of availableShortcuts.value) {
    if (shortcut.keyword?.includes(input.value.toLowerCase())) {
      shortcuts.push(shortcut)
    }
  }

  return shortcuts.length ? shortcuts : availableShortcuts.value
})

function shortcutProps(shortcut: Shortcut) {
  if (shortcut.route) return { to: shortcut.route }
  if (shortcut.externalLink) return { href: shortcut.externalLink, target: '_blank' }
  return {}
}

const selectedShortcut = ref<number>(0)

watch(matchedShortcuts, (value) => {
  /*
   * When the current displayed matching shortcuts change,
   * the currently selected shortcut might be out of bounds, so we reset to the first one
   */
  if (value.length <= selectedShortcut.value) selectedShortcut.value = 0
})

function navigate(event: KeyboardEvent) {
  if (!matchedShortcuts.value.length) return
  switch (event.key) {
    case 'Escape':
      inputElement.value?.blur()
      break
    case 'Enter':
      const shortcut = matchedShortcuts.value[selectedShortcut.value] ?? matchedShortcuts.value[0]
      if (shortcut.route) {
        router.push(shortcut.route)
        inputElement.value?.blur()
        input.value = ''
      } else if (shortcut.externalLink) {
        window.open(shortcut.externalLink, '_blank')
        inputElement.value?.blur()
        input.value = ''
      }
      break
    case 'ArrowDown':
      // Go one shortcut up, which could go us out of bounds, so we use a modulo to loop back to the beginning
      selectedShortcut.value = (selectedShortcut.value + 1) % matchedShortcuts.value.length
      break
    case 'ArrowUp':
      /*
       * Go one shortcut up, which could go us out of bounds, so we use a modulo to loop back to the end of the list.
       * Unlike in Python, the JS implementation of a modulo will allow negative values: `(0 - 1) % 4` returns `-1`.
       * We therefore add the total length once before computing a modulo: `(0 + 4 - 1) % 4` will return `3`.
       */
      selectedShortcut.value =
        (selectedShortcut.value + matchedShortcuts.value.length - 1) % matchedShortcuts.value.length
      break
  }
}

onMounted(() => {
  Mousetrap.bind('g', (event: Event) => {
    // Try to skip this shortcut when a modal is opened, otherwise we would bring focus outside of the modal
    if (document.querySelector('.modal.is-active')) return
    // Prevent "g" from actually being typed into the command bar
    event.preventDefault()
    inputElement.value?.focus()
  })
})
onUnmounted(() => {
  Mousetrap.unbind('g')
})
</script>

<style lang="scss">
.goto-input {
  position: relative;

  & input {
    transition: background 0.5s ease;

    &:not(:focus) {
      background: none;
      border: none;
      color: white;
      &::placeholder {
        color: white;
        opacity: 50%;
      }
    }
  }

  & kbd {
    display: none;
  }
  &:hover:not(:focus-within) kbd {
    display: initial;
    position: absolute;
    right: 0;
    opacity: 50%;
    border-width: 1px;
    margin: 0.5rem;
  }

  // Make the dropdown visible whenever the input is focused
  &:focus-within + .navbar-dropdown {
    display: block;
  }
}

.navbar-dropdown:active,
.navbar-dropdown:has(:active) {
  display: block;
}

.navbar-dropdown > .navbar-item {
  --bulma-strong-color: white;
  white-space-collapse: preserve;
}
</style>
